import type { CurrentDependencyItemDto } from "../../../api/dependencies";
import { formatDisplayDateValue } from "../../../utils/dateDisplay";
import { buildInferredReasonLabel } from "../../../utils/inferredRiskReason";
import { ClientTable, Tabs } from "../../shared";
import {
  BulletList,
  DetailAccordionGroup,
  DetailAccordionSection,
  DetailKeyMessage,
  DetailMetadataStrip,
  DetailModalShell,
  DetailStatusBadge,
  DetailSubsection,
  EvidenceReferenceList,
  LinkedItems,
  PriorityBadge,
  StatusBadge,
  TagList,
} from "../AiInsightsRaidDetailParts";
import type {
  RaidDependenciesClashDto,
  RaidDependenciesTopItemDto,
} from "./AiInsightsRaidDependenciesTypes";

export type AiInsightsRaidDependenciesDetailModalProps = {
  item: RaidDependenciesTopItemDto;
  sourceDependency?: CurrentDependencyItemDto;
  clashes: RaidDependenciesClashDto[];
  onClose: () => void;
};

function display(value?: string | number | null) {
  return value == null || value === "" ? "Not available" : String(value);
}

function StatusTag({ value }: { value?: string | null }) {
  if (value == null || value === "") {
    return <span>—</span>;
  }
  return <StatusBadge value={value} />;
}

function displayDate(value?: string | null) {
  return value == null || value.trim() === ""
    ? "Not available"
    : formatDisplayDateValue(value, { fallback: "Not available" });
}

function formatBoolean(value?: boolean | null) {
  return value == null ? "Not available" : value ? "Yes" : "No";
}

function hasValue(value?: string | number | boolean | null) {
  return value != null && value !== "";
}

function formatInferredStatus(value?: string | null) {
  return value?.replace(/[_-]+/g, " ").replace(/\b\w/g, (letter) => letter.toUpperCase());
}

export function AiInsightsRaidDependenciesDetailModal({
  item,
  sourceDependency,
  clashes,
  onClose,
}: AiInsightsRaidDependenciesDetailModalProps) {
  const statusCurrency = item.status_currency_assessment;
  const schedule = item.schedule_assessment;
  const control = item.control_and_resolution_assessment;
  const requirement = item.dependency_requirement;
  const dependencyStatus = [
    sourceDependency?.status,
    sourceDependency?.normalizedStatus,
    item.status,
  ].find((value): value is string => value != null && value.trim() !== "");
  const inferredStatus = formatInferredStatus(sourceDependency?.inferredStatus);
  const inferredReason = sourceDependency == null
    ? undefined
    : buildInferredReasonLabel(
        sourceDependency.riskReasonCodes ?? [],
        sourceDependency.riskMetrics?.insufficientDataReasons ?? []
      );
  const dependencyPriority = item.priority_band ?? (item.rank != null ? `#${item.rank}` : null);
  const dependencyStrength = sourceDependency?.strength ?? item.dependency_strength;
  const sourceDueDate = sourceDependency?.dateDue;
  const dueDate =
    sourceDueDate != null && sourceDueDate.trim() !== ""
      ? sourceDueDate
      : schedule?.required_by_date;
  const provider = display(
    requirement?.provider_or_owner ??
      (sourceDependency?.owner != null
        ? `${sourceDependency.workstream ?? item.providing_workstream ?? "Provider not available"}; owner ${sourceDependency.owner}`
        : (sourceDependency?.workstream ?? item.providing_workstream))
  );
  const receiver = display(
    requirement?.receiver_or_dependent_owner ??
      sourceDependency?.dependentWorkstream ??
      item.dependent_workstream
  );
  const detailTitle = item.title ?? sourceDependency?.description ?? "Not available";
  const dependencySubtitle =
    sourceDependency?.description != null &&
    sourceDependency.description.trim() !== "" &&
    sourceDependency.description !== detailTitle &&
    sourceDependency.description !== requirement?.prerequisite
      ? sourceDependency.description
      : null;
  const whyThisMatters = [item.why_it_is_important_now, item.why_priority_for_this_user].filter(
    (value): value is string => value != null && value.trim().length > 0
  );
  const impact = item.impact_analysis;
  const linkedItems = Array.isArray(item.linked_items) ? item.linked_items : [];
  const currencyRows = [
    {
      lastUpdate: statusCurrency?.last_update_date,
      daysSince: statusCurrency?.days_since_last_update,
      threshold: statusCurrency?.stale_record_threshold_days,
      stale: formatBoolean(statusCurrency?.stale_update_flag),
      assessment: statusCurrency?.assessment,
    },
  ];
  const scheduleRows = [
    {
      predecessor: schedule?.predecessor_item_id,
      predecessorDate: schedule?.predecessor_available_date,
      predecessorBasis: schedule?.predecessor_date_basis,
      dependent: schedule?.dependent_item_id,
      buffer: schedule?.slack_days,
      requiredByBasis: schedule?.required_by_basis,
    },
  ];
  const controlRows = [
    {
      ownership: control?.ownership,
      dueDateQuality: control?.due_date_quality,
      resolutionPlan: control?.resolution_plan,
      updateCurrency: control?.update_currency,
      escalationPosition: control?.escalation_position,
    },
  ];
  const hasControlAssessment = [
    control?.ownership,
    control?.due_date_quality,
    control?.resolution_plan,
    control?.update_currency,
    control?.escalation_position,
    control?.ownership_description,
    control?.due_date_quality_description,
    control?.resolution_plan_description,
    control?.update_currency_description,
    control?.escalation_position_description,
    control?.summary,
  ].some(hasValue);
  const needsStatusReview =
    statusCurrency?.status_review_needed === true ||
    statusCurrency?.stale_update_flag === true ||
    statusCurrency?.status_change_expected_but_stale === true;
  const statusReviewBadge = needsStatusReview ? "Review needed" : statusCurrency?.assessment;
  const evidenceIds = item.evidence_ids ?? [];

  return (
    <DetailModalShell
      title={detailTitle}
      onClose={onClose}
      className="global-insights-overlay__risk-detail-modal--dependency"
      lastUpdatedAt={sourceDependency?.last_updated_at ?? sourceDependency?.lastUpdatedAt}
    >
      {hasValue(dependencySubtitle) && (
        <p className="global-insights-overlay__risk-detail-subtitle">{dependencySubtitle}</p>
      )}

      <DetailMetadataStrip
        inline
        items={[
          {
            label: "ID",
            value: display(sourceDependency?.sourceDepId ?? item.dependency_id),
          },
          {
            label: "Priority",
            value: (
              <span className="global-insights-overlay__risk-detail-metadata-value">
                <PriorityBadge value={dependencyPriority} />
                {hasValue(dependencyPriority) && (
                  <span className="global-insights-overlay__risk-detail-inference-label">
                    AI assessed
                  </span>
                )}
              </span>
            ),
          },
          {
            label: "Status",
            value: (
              <span className="global-insights-overlay__risk-detail-metadata-value">
                <StatusBadge value={dependencyStatus} />
              </span>
            ),
          },
          {
            label: "Due",
            value: (
              <span className="global-insights-overlay__risk-detail-metadata-value">
                {displayDate(dueDate)}
                {!hasValue(sourceDueDate) && hasValue(dueDate) && (
                  <span className="global-insights-overlay__risk-detail-inference-label">
                    AI assessed
                  </span>
                )}
              </span>
            ),
          },
          { label: "Strength", value: display(dependencyStrength) },
          { label: "Date raised", value: displayDate(sourceDependency?.dateRaised) },
        ]}
      />
      <DetailMetadataStrip
        items={[
          { label: "Provider", value: provider },
          { label: "Receiver", value: receiver },
        ]}
      />
      {hasValue(inferredStatus) && (
        <div className="global-insights-overlay__risk-detail-inference-row" aria-label="Inferred status">
          <DetailStatusBadge value={inferredStatus} />
          {hasValue(inferredReason) && <span>{inferredReason}</span>}
        </div>
      )}

      <DetailKeyMessage label="Prerequisite">{display(requirement?.prerequisite)}</DetailKeyMessage>

      <Tabs
        variant="underline"
        className="global-insights-overlay__risk-detail-tabs global-insights-overlay__risk-detail-tabs--dependency"
        items={[
          {
            key: "requirement",
            label: "Requirement",
            content: (
              <DetailAccordionGroup>
                <DetailAccordionSection heading="Requirement details" itemKey="requirement-details">
                  <div className="global-insights-overlay__risk-detail-requirement-list">
                    <DetailSubsection label="Acceptance condition">
                      <p className="global-insights-overlay__risk-detail-text">
                        {display(requirement?.acceptance_or_completion_condition)}
                      </p>
                    </DetailSubsection>
                    <DetailSubsection label="Requirement unknowns">
                      <TagList items={requirement?.unknowns ?? []} />
                    </DetailSubsection>
                    {hasValue(item.validation_question) && (
                      <DetailSubsection label="Validation question">
                        <p className="global-insights-overlay__risk-detail-text">
                          {display(item.validation_question)}
                        </p>
                      </DetailSubsection>
                    )}
                  </div>
                </DetailAccordionSection>
                <DetailAccordionSection heading="Why this matters" itemKey="why-matters">
                  <BulletList items={whyThisMatters} />
                </DetailAccordionSection>
              </DetailAccordionGroup>
            ),
          },
          {
            key: "schedule",
            label: "Schedule & status",
            content: (
              <DetailAccordionGroup>
                <DetailAccordionSection
                  heading="Status review & unknowns"
                  itemKey="status-review"
                  statusBadge={<DetailStatusBadge value={statusReviewBadge} />}
                >
                  <ClientTable
                    className="global-insights-overlay__risk-detail-table global-insights-overlay__risk-detail-table--borderless"
                    data={currencyRows}
                    rowKey={() => "currency"}
                    columns={[
                      {
                        id: "lastUpdate",
                        header: "Last update date",
                        renderCell: (row) => displayDate(row.lastUpdate),
                      },
                      {
                        id: "daysSince",
                        header: "Days since last update",
                        renderCell: (row) => display(row.daysSince),
                      },
                      {
                        id: "threshold",
                        header: "Stale-record threshold",
                        renderCell: (row) => display(row.threshold),
                      },
                      { id: "stale", header: "Record appears stale", accessor: "stale" },
                      {
                        id: "assessment",
                        header: "Overall assessment",
                        renderCell: (row) => <StatusTag value={row.assessment} />,
                      },
                    ]}
                  />
                  {hasValue(statusCurrency?.explanation) && (
                    <DetailSubsection label="Explanation">
                      <p className="global-insights-overlay__risk-detail-text">
                        {statusCurrency?.explanation}
                      </p>
                    </DetailSubsection>
                  )}
                  {hasValue(statusCurrency?.expected_change_trigger) && (
                    <DetailSubsection label="Expected change trigger">
                      <p className="global-insights-overlay__risk-detail-text">
                        {statusCurrency?.expected_change_trigger}
                      </p>
                    </DetailSubsection>
                  )}
                  {statusCurrency?.status_change_expected_but_stale != null && (
                    <DetailSubsection label="Expected change appears stale">
                      <p className="global-insights-overlay__risk-detail-text">
                        {formatBoolean(statusCurrency?.status_change_expected_but_stale)}
                      </p>
                    </DetailSubsection>
                  )}
                  {statusCurrency?.status_review_needed != null && (
                    <DetailSubsection label="Status review needed">
                      <p className="global-insights-overlay__risk-detail-text">
                        {formatBoolean(statusCurrency?.status_review_needed)}
                      </p>
                    </DetailSubsection>
                  )}
                  {hasValue(statusCurrency?.review_question) && (
                    <DetailSubsection label="Review question">
                      <p className="global-insights-overlay__risk-detail-text">
                        {statusCurrency?.review_question}
                      </p>
                    </DetailSubsection>
                  )}
                  {(statusCurrency?.contradictory_evidence_ids?.length ?? 0) > 0 && (
                    <DetailSubsection label="Contradictory evidence">
                      <EvidenceReferenceList items={statusCurrency!.contradictory_evidence_ids!} />
                    </DetailSubsection>
                  )}
                </DetailAccordionSection>

                <DetailAccordionSection
                  heading="Schedule"
                  itemKey="schedule-details"
                  statusBadge={<DetailStatusBadge value={schedule?.alignment_status} />}
                >
                  <ClientTable
                    className="global-insights-overlay__risk-detail-table global-insights-overlay__risk-detail-table--borderless"
                    data={scheduleRows}
                    rowKey={() => "schedule"}
                    columns={[
                      {
                        id: "predecessor",
                        header: "Predecessor item",
                        renderCell: (row) => display(row.predecessor),
                      },
                      {
                        id: "predecessorDate",
                        header: "Predecessor date",
                        renderCell: (row) => displayDate(row.predecessorDate),
                      },
                      {
                        id: "predecessorBasis",
                        header: "Predecessor basis",
                        renderCell: (row) => display(row.predecessorBasis),
                      },
                      {
                        id: "dependent",
                        header: "Dependent item",
                        renderCell: (row) => display(row.dependent),
                      },
                      {
                        id: "buffer",
                        header: "Days of schedule buffer",
                        renderCell: (row) => display(row.buffer),
                      },
                      {
                        id: "requiredByBasis",
                        header: "Required-by basis",
                        renderCell: (row) => display(row.requiredByBasis),
                      },
                    ]}
                  />
                  <DetailSubsection label="Summary">
                    <p className="global-insights-overlay__risk-detail-text">
                      {display(schedule?.summary ?? schedule?.explanation)}
                    </p>
                  </DetailSubsection>
                  <DetailSubsection label="Timing explanation">
                    <p className="global-insights-overlay__risk-detail-text">
                      {display(schedule?.timing_explanation ?? schedule?.explanation)}
                    </p>
                  </DetailSubsection>
                </DetailAccordionSection>
              </DetailAccordionGroup>
            ),
          },
          {
            key: "impact",
            label: "Impact & response",
            content: (
              <DetailAccordionGroup>
                <DetailAccordionSection heading="Impact" itemKey="impact">
                  <div className="global-insights-overlay__risk-detail-grey-box global-insights-overlay__risk-detail-impact-panel">
                    <DetailSubsection label="Observed impact occurring">
                      <p className="global-insights-overlay__risk-detail-text">
                        {display(impact?.observed_impact)}
                      </p>
                    </DetailSubsection>
                    <DetailSubsection label="Potential future impact">
                      <p className="global-insights-overlay__risk-detail-text">
                        {display(impact?.potential_impact)}
                      </p>
                    </DetailSubsection>
                    <DetailSubsection label="Resulting risk or issue">
                      <p className="global-insights-overlay__risk-detail-text">
                        {display(impact?.resulting_risk_or_issue)}
                      </p>
                    </DetailSubsection>
                    <DetailSubsection label="Affected workstreams">
                      <TagList items={impact?.affected_workstreams ?? []} />
                    </DetailSubsection>
                    <DetailSubsection label="Affected milestones">
                      <TagList items={impact?.affected_milestones_or_activities ?? []} />
                    </DetailSubsection>
                    <DetailSubsection label="Affected objectives">
                      <TagList items={impact?.affected_objectives_or_value ?? []} />
                    </DetailSubsection>
                  </div>
                </DetailAccordionSection>

                <DetailAccordionSection
                  heading="Ownership & resolution"
                  itemKey="ownership-resolution"
                  statusBadge={
                    control?.resolution_plan != null ? (
                      <DetailStatusBadge value={`${control.resolution_plan} controls`} />
                    ) : null
                  }
                >
                  {hasControlAssessment ? (
                    <>
                      {hasValue(control?.summary) && (
                        <p className="global-insights-overlay__risk-detail-grey-box">
                          {control?.summary}
                        </p>
                      )}
                      <ClientTable
                        className="global-insights-overlay__risk-detail-table global-insights-overlay__risk-detail-table--borderless"
                        data={controlRows}
                        rowKey={() => "control"}
                        columns={[
                          {
                            id: "ownership",
                            header: "Ownership",
                            renderCell: (row) => <StatusTag value={row.ownership} />,
                          },
                          {
                            id: "dueDateQuality",
                            header: "Due date quality",
                            renderCell: (row) => <StatusTag value={row.dueDateQuality} />,
                          },
                          {
                            id: "resolutionPlan",
                            header: "Resolution plan",
                            renderCell: (row) => <StatusTag value={row.resolutionPlan} />,
                          },
                          {
                            id: "updateCurrency",
                            header: "Update currency",
                            renderCell: (row) => <StatusTag value={row.updateCurrency} />,
                          },
                          {
                            id: "escalationPosition",
                            header: "Escalation position",
                            renderCell: (row) => display(row.escalationPosition),
                          },
                        ]}
                      />
                      {hasValue(control?.ownership_description) && (
                        <DetailSubsection label="Ownership">
                          <p className="global-insights-overlay__risk-detail-text">
                            {control?.ownership_description}
                          </p>
                        </DetailSubsection>
                      )}
                      {hasValue(control?.due_date_quality_description) && (
                        <DetailSubsection label="Due date quality">
                          <p className="global-insights-overlay__risk-detail-text">
                            {control?.due_date_quality_description}
                          </p>
                        </DetailSubsection>
                      )}
                      {hasValue(control?.resolution_plan_description) && (
                        <DetailSubsection label="Resolution plan">
                          <p className="global-insights-overlay__risk-detail-text">
                            {control?.resolution_plan_description}
                          </p>
                        </DetailSubsection>
                      )}
                      {hasValue(control?.update_currency_description) && (
                        <DetailSubsection label="Update currency">
                          <p className="global-insights-overlay__risk-detail-text">
                            {control?.update_currency_description}
                          </p>
                        </DetailSubsection>
                      )}
                      {hasValue(control?.escalation_position_description) && (
                        <DetailSubsection label="Escalation position">
                          <p className="global-insights-overlay__risk-detail-text">
                            {control?.escalation_position_description}
                          </p>
                        </DetailSubsection>
                      )}
                    </>
                  ) : (
                    <p className="global-insights-overlay__risk-detail-text">
                      No control assessment is available.
                    </p>
                  )}
                </DetailAccordionSection>
                <DetailAccordionSection heading="Recommended actions" itemKey="recommended-actions">
                  <BulletList
                    items={item.recommended_focus != null ? [item.recommended_focus] : []}
                  />
                </DetailAccordionSection>
              </DetailAccordionGroup>
            ),
          },
          {
            key: "related",
            label: `Related${linkedItems.length + clashes.length + evidenceIds.length > 0 ? ` (${linkedItems.length + clashes.length + evidenceIds.length})` : ""}`,
            content: (
              <DetailAccordionGroup>
                <DetailAccordionSection
                  heading="Related items"
                  itemKey="related-items"
                  badge={linkedItems.length}
                >
                  <LinkedItems items={linkedItems} />
                </DetailAccordionSection>

                <DetailAccordionSection heading="Clashes" itemKey="clashes" badge={clashes.length}>
                  {clashes.length === 0 ? (
                    <p className="global-insights-overlay__risk-detail-text">
                      No clashes identified.
                    </p>
                  ) : (
                    clashes.map((clash, index) => (
                      <div
                        key={clash.clash_id ?? index}
                        className="global-insights-overlay__risk-detail-grey-box"
                      >
                        <div className="global-insights-overlay__risk-detail-text">
                          <PriorityBadge value={clash.severity} /> {display(clash.clash_type)} ·
                          Confidence: {display(clash.confidence)}
                        </div>
                        <DetailSubsection label="Related dependency">
                          <p className="global-insights-overlay__risk-detail-text">
                            {display(clash.related_dependency_name ?? clash.related_dependency_id)}
                          </p>
                        </DetailSubsection>
                        <DetailSubsection label="Predecessor activity">
                          <p className="global-insights-overlay__risk-detail-text">
                            {display(clash.predecessor_activity_title ?? clash.predecessor_item_id)}{" "}
                            — {displayDate(clash.predecessor_date)}
                          </p>
                        </DetailSubsection>
                        <DetailSubsection label="Dependent activity">
                          <p className="global-insights-overlay__risk-detail-text">
                            {display(clash.dependent_activity_title ?? clash.dependent_item_id)} —{" "}
                            {displayDate(clash.required_by_date)}
                          </p>
                        </DetailSubsection>
                        <DetailSubsection label="Why flagged">
                          <p className="global-insights-overlay__risk-detail-text">
                            {display(clash.why_flagged)}
                          </p>
                        </DetailSubsection>
                        <DetailSubsection label="Potential impact">
                          <p className="global-insights-overlay__risk-detail-text">
                            {display(clash.possible_impact)}
                          </p>
                        </DetailSubsection>
                        <DetailSubsection label="Available schedule buffer">
                          <p className="global-insights-overlay__risk-detail-text">
                            {display(clash.slack_days)} days
                          </p>
                        </DetailSubsection>
                        <DetailSubsection label="Recommended actions">
                          <BulletList
                            items={(clash.recommended_actions ?? []).map(
                              (action) =>
                                `${display(action.title)} — ${display(action.description)}`
                            )}
                            emptyText={display(clash.resolution_needed)}
                          />
                        </DetailSubsection>
                      </div>
                    ))
                  )}
                </DetailAccordionSection>

                <DetailAccordionSection heading="Evidence" itemKey="evidence">
                  <EvidenceReferenceList items={evidenceIds} />
                </DetailAccordionSection>
              </DetailAccordionGroup>
            ),
          },
        ]}
      />
    </DetailModalShell>
  );
}

export default AiInsightsRaidDependenciesDetailModal;
